/* eslint-disable init-declarations */
import React, { useState, useEffect, useRef } from 'react';
import { Grid } from '@mui/material';
import HomeHeaderLayout from 'components/Common/Layouts/ModuleHeaderLayout';
import { Link } from 'react-router-dom';
import BiteUAPI from 'api/BITEUAPI';
import { useSnackbar } from 'notistack';
import ContentError from 'components/Common/ContentError';

const ResourcesHome: React.FC = () => {
  // Page State and Functions
  const [ courses, setCourses ] = useState<{
    isLoaded: boolean;
    data: any[];
    error: string | undefined;
  }>({ isLoaded: false, data: [], error: undefined });

  const [ userStatus, setUserStatus ] = useState<{
    isLoaded: boolean;
    data: any;
    error: string | undefined;
  }>({ isLoaded: false, data: {}, error: undefined });

  // const [ cur_lesson, setCurLesson ] = useState<any>({ });
  const [ curCourse, setCurCourse ] = useState<any>({ });
  const libRef = useRef<any>(null);

  const executeScroll = () => {
    if (libRef && libRef.current) {
      libRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const { enqueueSnackbar } = useSnackbar();

  const checkCourse = (name: string, arr: any) => {
    const completed = arr.filter((c: any) => {
      if (c === null) {
        return false;
      }
      return c.name === name;
    });
    if (completed.length) {
      return true;
    }
    return false;
  };

  const getInfo = async() => {
    // COURSE INFO
    const courseResp = await BiteUAPI.getCourses();
    if (courseResp?.success) {
      if (!courseResp.result.length) {
        const error_msg = `No Courses in the Database: ${courseResp?.result}`;
        enqueueSnackbar(error_msg, { variant: 'error' });
        setCourses({ data: [], isLoaded: true, error: error_msg });
      } else {
        setCourses({ data: courseResp?.result, isLoaded: true, error: undefined });
      }
    } else if (courseResp) {
      const error_msg = `Error retrieving courses: ${courseResp?.result}`;
      enqueueSnackbar(error_msg, { variant: 'error' });
      setCourses({ data: [], isLoaded: true, error: error_msg });
    }

    // USER INFO
    const userResp = await BiteUAPI.getUserTrainingStatus();
    if (userResp?.success) {
      if (userResp?.result.length === 0 || userResp?.result[0]?.progress[0]?.currentLesson) {
        const progArr: {
          course: any;
          currentProgress: any;
         }[] = [];

        courseResp?.result.forEach((c: any) => {
          const statusArr: {
            lesson: any;
            currentTime: number;
            currentPercent: number;
          }[] = [];

          c.lessons.forEach((les: any) => {
            statusArr.push({
              lesson: les,
              currentTime: 0,
              currentPercent: 0
            });
          });
          progArr.push({ course: c.name, currentProgress: statusArr });
        });

        let res: any;
        if (userResp?.result.length === 0) {
          res = await BiteUAPI.createUserTrainingStatus({ newStatus: { progress: progArr } });
        } else {
          const body = userResp.result[0];
          body.progress = progArr;
          res = await BiteUAPI.updateUserTrainingStatus(body);
        }

        if (res?.success) {
          setUserStatus({ data: res?.result, isLoaded: true, error: undefined });
          setCurCourse(courseResp?.result[0]);
        } else {
          const error_msg = `Error creating user training status: ${userResp?.result}`;
          enqueueSnackbar(error_msg, { variant: 'error' });
        }
      } else {
        setUserStatus({ data: userResp?.result[0], isLoaded: true, error: undefined });
        if (userResp.result[0].progress.length) {
          const nonCompletedCourses = courseResp?.result.filter((c: any) => {
            return !checkCourse(c.name, userResp.result[0].completed);
          });
          setCurCourse(nonCompletedCourses[0]);
        } else {
          setCurCourse(courseResp?.result[0]);
        }
      }
    } else {
      const error_msg = `Error retrieving user training status: ${userResp?.result}`;
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
  };

  useEffect(() => {
    getInfo();
  }, []);

  if (!courses.isLoaded || !userStatus.isLoaded || !curCourse || Object.keys(curCourse).length === 0) {
    return (
      <div className='spinner-spacer-bite-u'>
        <div className='loader'>Loading...</div>
      </div>
    );
  }
  if (courses.error) {
    return <ContentError error={courses.error} />;
  }

  const completeTag =
  <div className='complete-tag-bite-u'>
    <p className='props-tag-text'>Completed</p>
  </div>;

  const progressTag =
  <div className='progress-tag-bite-u'>
    <p className='props-tag-text'>In Progress</p>
  </div>;

  const helpContent = <p className='module-header-tooltip-text'>
In BITE U there are &quot;How To&quot; videos that help users maximize the tools provided to you by BITE. There are also industry specific courses that instruct users on various topics related to compliance.
  </p>;

  return (
    <HomeHeaderLayout title='BITE U' helpContent={helpContent}>
      <Grid container spacing={2} style={{ marginBottom: '70px' }}>
        <Grid item xs={12} md={6}>
          <div className='bite-u-section'>
            <h1>Industry Training</h1>
            <div className='bite-u-card-top'>
              <div className='bite-u-card-video'>
              </div>
              <div className='bite-u-card-top-content'>
                <h2>6 Courses</h2>
                <h3>Learn how to properly utilize BITE across the industry</h3>
                <h4 onClick={executeScroll}>Explore Library</h4>
              </div>
            </div>
          </div>
        </Grid>
        <Grid item xs={12} md={6}>
          <div className='bite-u-section'>
            <h1>App How-to Guides</h1>
            <div className='bite-u-card-top'>
              <div className='bite-u-card-video'>
              </div>
              <div className='bite-u-card-top-content'>
                <h2>11 Videos</h2>
                <h3>Learn how to utilize the many features within BITE</h3>
                <Link to='/training-videos'>Visit</Link>
              </div>
            </div>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className='bite-u-section' ref={libRef}>
            <h1>Library</h1>
          </div>
        </Grid>
        { courses?.data?.map((course, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <div className='bite-u-card-bottom'>
              <div className='train-card-pic'>
                <img src={course.imgLink} alt='img not found' width='100%' height={240} />
                {checkCourse(course.name, userStatus.data.completed) ? completeTag : checkCourse(course.name, userStatus.data.current) && progressTag}
              </div>
              <div className='train-card-content'>
                <div className='train-card-content-info'>
                  <h1>{course?.lessons.length} Videos</h1>
                  <div className='train-card-content-spacer'>
                    <div className='train-card-title-spacer'>
                      <h2>{course.name}</h2>
                      <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
                        <path d='M7 17L17 7M17 7H7M17 7V17' stroke='#EFF1F6' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                      </svg>
                    </div>
                    <p>{course.blurb}</p>
                  </div>
                </div>
                <Link to={{
                  pathname: '/bite-u/course-info',
                  state: {
                    course_id: course._id,
                  }
                }}>View Course</Link>
              </div>
            </div>
          </Grid>
        ))}
        {/* <Grid item xs={12}>
          <div className='bite-u-section'>
            <h1>Certificates of Completion</h1>
          </div>
        </Grid> */}
      </Grid>
    </HomeHeaderLayout>
  );
};

export default ResourcesHome;
